import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Layout from '../components/Layout';
import Footer from '../components/Footer';

// Import all screenshot images
import activesessions from '../assets/screenshots/activesessions.jpg';
import profiles from '../assets/screenshots/profiles.jpg';
import scheduleview from '../assets/screenshots/scheduleview.jpg';
import history from '../assets/screenshots/history.jpg';
import guide from '../assets/screenshots/guide.jpg';
import support from '../assets/screenshots/support.jpg';
import logs from '../assets/screenshots/logs.jpg';
import schedulereation from '../assets/screenshots/schedulereation.jpg';
import dessiondetails from '../assets/screenshots/dessiondetails.jpg';
import update from '../assets/screenshots/update.jpg';

// Add enhanced styled components from BuyPage
const StatsBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin: 2rem 0;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%);
  border-radius: 16px;
  border: 1px solid rgba(16, 185, 129, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.2rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
  color: #fff;
`;

const StatNumber = styled.div`
  font-size: 1.8rem;
  font-weight: 900;
  color: #10b981;
  text-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #b6d0ff;
  margin-top: 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TestimonialSection = styled.div`
  margin: 3rem 0 2rem 0;
  text-align: center;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    margin: 2rem auto 1.5rem auto;
  }
`;

const TestimonialTitle = styled.h3`
  font-size: 1.8rem;
  color: #fff;
  margin-bottom: 2rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const TestimonialCard = styled.div`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(110, 168, 255, 0.15);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const TestimonialText = styled.p`
  font-size: 0.95rem;
  color: #e1e9ff;
  line-height: 1.5;
  font-style: italic;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const TestimonialAuthor = styled.div`
  color: #6ea8ff;
  font-weight: 600;
  font-size: 0.85rem;
`;

const TrustSignals = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin: 2rem auto;
  padding: 1.5rem;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  border: 1px solid rgba(110, 168, 255, 0.1);
  max-width: 1000px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1.2rem;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #b6d0ff;
  font-size: 0.9rem;
  
  &:before {
    content: '✓';
    color: #10b981;
    font-weight: bold;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const UrgencyBanner = styled.div`
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  color: white;
  text-align: center;
  padding: 0.8rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }
`;

const DetailedFeaturesSection = styled.div`
  margin: 3rem 0;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 0 1rem;
  }
`;

const DetailedFeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border-radius: 24px;
  padding: 3rem 2rem;
  border: 1px solid rgba(110, 168, 255, 0.2);
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  text-align: center;
  color: #fff;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const DetailedFeaturesTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #fff;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const DetailedFeaturesDescription = styled.p`
  color: #b6d0ff;
  font-size: 1.15rem;
  margin: 0 auto 2rem auto;
  max-width: 700px;
  line-height: 1.7;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled components
const PageBackground = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow-x: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const HeaderSection = styled.div`
  width: 100%;
  padding: 3rem 2rem 1rem 2rem;
  position: relative;
  z-index: 2;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem 1rem 1rem;
  }
`;

const HeaderContainer = styled.div`
  max-width: 900px;
  margin: 0 auto 2rem auto;
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  color: transparent;
  background: linear-gradient(135deg, #6ea8ff 0%, #ffffff 50%, #3a8fff 100%);
  background-clip: text;
  -webkit-background-clip: text;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderDescription = styled.p`
  font-size: 1.2rem;
  color: #b6d0ff;
  margin-bottom: 2rem;
  line-height: 1.6;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const PricingInfo = styled.div`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 16px;
  padding: 1.5rem;
  margin: 2rem auto;
  max-width: 400px;
  backdrop-filter: blur(10px);
`;

const PricingTitle = styled.h3`
  color: #10b981;
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PricingDetails = styled.p`
  color: #b6d0ff;
  font-size: 1rem;
  margin-bottom: 0;
  opacity: 0.9;
`;

const HeaderButton = styled(Link)`
  display: inline-block;
  padding: 1.2rem 3rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
    color: #fff;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1.1rem;
    width: 100%;
    max-width: 300px;
  }
`;

const FeatureSection = styled.div`
  width: 100%;
  padding: 2rem 2rem 1rem 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 1rem 1rem 0.5rem 1rem;
  }
`;

const FeatureContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FeatureCard = styled.div`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(110, 168, 255, 0.15);
  backdrop-filter: blur(10px);
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  color: #6ea8ff;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.8rem;
`;

const FeatureDescription = styled.p`
  color: #b6d0ff;
  font-size: 0.9rem;
  line-height: 1.5;
  opacity: 0.9;
`;

const CarouselSection = styled.div`
  width: 100%;
  padding: 1rem 2rem 2rem 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem 1rem 1rem;
  }
`;

const CarouselTitle = styled.h2`
  text-align: center;
  font-size: 1.8rem;
  color: #fff;
  margin-bottom: 2rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const CarouselContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  background: transparent;
  border-radius: 24px;
  overflow: hidden;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 800px;
  object-fit: contain;
  position: ${props => props.index === 0 ? 'relative' : 'absolute'};
  top: ${props => props.index === 0 ? 'auto' : '0'};
  left: ${props => props.index === 0 ? 'auto' : '0'};
  opacity: ${props => props.$active ? '1' : '0'};
  transition: opacity 0.5s ease-in-out;
  border-radius: 24px;
  
  @media (max-width: 768px) {
    height: 500px;
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? '#6ea8ff' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #6ea8ff;
    transform: scale(1.2);
  }
`;



// Screenshot images array
const screenshots = [
  { src: profiles, alt: "Profile Management Interface" },
  { src: activesessions, alt: "Active Sessions Dashboard" },
  { src: scheduleview, alt: "Schedule Management View" },
  { src: schedulereation, alt: "Schedule Creation Interface" },
  { src: history, alt: "Session History Tracking" },
  { src: dessiondetails, alt: "Detailed Session Information" },
  { src: logs, alt: "Activity Logs and Monitoring" },
  { src: guide, alt: "Built-in Guides and Documentation" },
  { src: support, alt: "Support and Help Interface" },
  { src: update, alt: "Update and Maintenance Features" }
];

function WarmerPage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % screenshots.length
      );
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <PageBackground>
      <BackgroundPattern />
      <Layout>
        <HeaderSection>
          <HeaderContainer>
            <UrgencyBanner>
              🔥 Join 300+ users getting better results daily • Proven warming system
            </UrgencyBanner>
            <HeaderTitle>DESERVEDWARMER</HeaderTitle>
            <HeaderDescription>
              Stop account suspensions forever. Our advanced automation creates human-like browsing patterns that Amazon, eBay, and Vinted 
              recognize as genuine user behavior. No more instant bans or flagged accounts.
            </HeaderDescription>
            <StatsBar>
              <StatItem>
                <StatNumber>300+</StatNumber>
                <StatLabel>Active Users</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>100%</StatNumber>
                <StatLabel>Success Rate</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>24/7</StatNumber>
                <StatLabel>Support</StatLabel>
              </StatItem>
            </StatsBar>
            <PricingInfo>
              <PricingTitle>Starting at $60/month</PricingTitle>
              <PricingDetails>
                Flexible plans available • Cancel anytime • Instant access
              </PricingDetails>
            </PricingInfo>
            <HeaderButton to="/buy">
              GET STARTED NOW
            </HeaderButton>
          </HeaderContainer>
        </HeaderSection>

        <DetailedFeaturesSection>
          <DetailedFeaturesContainer>
            <DetailedFeaturesTitle>Stop Account Suspensions Forever</DetailedFeaturesTitle>
            <DetailedFeaturesDescription>
              DeservedWarmer eliminates the #1 problem facing account creators: instant suspensions. Our advanced automation creates 
              realistic browsing patterns that platforms recognize as genuine user behavior.
            </DetailedFeaturesDescription>
            <FeatureContainer>
              <FeatureCard>
                <FeatureIcon>🤖</FeatureIcon>
                <FeatureTitle>Human-Like Automation</FeatureTitle>
                <FeatureDescription>
                  Realistic browsing, clicking, and scrolling patterns that perfectly mimic genuine users
                </FeatureDescription>
              </FeatureCard>
              <FeatureCard>
                <FeatureIcon>🛡️</FeatureIcon>
                <FeatureTitle>Platform Specific</FeatureTitle>
                <FeatureDescription>
                  Tailored warming strategies for Amazon, eBay, Vinted with platform-specific behaviors
                </FeatureDescription>
              </FeatureCard>
              <FeatureCard>
                <FeatureIcon>📊</FeatureIcon>
                <FeatureTitle>Smart Analytics</FeatureTitle>
                <FeatureDescription>
                  Track warming progress with detailed logs and session management tools
                </FeatureDescription>
              </FeatureCard>
              <FeatureCard>
                <FeatureIcon>🔒</FeatureIcon>
                <FeatureTitle>100% Private</FeatureTitle>
                <FeatureDescription>
                  Runs locally on your machine - your data never leaves your device
                </FeatureDescription>
              </FeatureCard>
              <FeatureCard>
                <FeatureIcon>⚡</FeatureIcon>
                <FeatureTitle>Advanced Scheduling</FeatureTitle>
                <FeatureDescription>
                  Set up complex warming schedules that run automatically based on your account needs
                </FeatureDescription>
              </FeatureCard>
              <FeatureCard>
                <FeatureIcon>🎯</FeatureIcon>
                <FeatureTitle>AdsPower Integration</FeatureTitle>
                <FeatureDescription>
                  Seamless integration with AdsPower browser profiles for professional multi-account management
                </FeatureDescription>
              </FeatureCard>
            </FeatureContainer>
          </DetailedFeaturesContainer>
        </DetailedFeaturesSection>

        <CarouselSection>
          <CarouselTitle>See DeservedWarmer in Action</CarouselTitle>
          <CarouselContainer>
            {screenshots.map((screenshot, index) => (
              <CarouselImage
                key={index}
                index={index}
                src={screenshot.src}
                alt={screenshot.alt}
                $active={index === currentImageIndex}
              />
            ))}
            <CarouselControls>
              {screenshots.map((_, index) => (
                <CarouselDot
                  key={index}
                  $active={index === currentImageIndex}
                  onClick={() => goToImage(index)}
                />
              ))}
            </CarouselControls>
          </CarouselContainer>
        </CarouselSection>

        <TestimonialSection>
          <TestimonialTitle>What Our Users Say</TestimonialTitle>
          <TestimonialGrid>
            <TestimonialCard>
              <TestimonialText>
                "Gone from doing 1 account every 2-3 weeks to doing 4-5 accounts every week. The warming patterns are so realistic, I haven't had a single suspension in 3 months."
              </TestimonialText>
              <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
            </TestimonialCard>
            <TestimonialCard>
              <TestimonialText>
                "Was skeptical at first but DeservedWarmer saved me from manual verification hell. Passed 12 verifications in a row without any issues. Worth every penny."
              </TestimonialText>
              <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
            </TestimonialCard>
            <TestimonialCard>
              <TestimonialText>
                "My Amazon accounts used to get flagged within days. Now they run for months without problems. The automation looks completely natural."
              </TestimonialText>
              <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
            </TestimonialCard>
          </TestimonialGrid>
        </TestimonialSection>

        <TrustSignals>
          <TrustItem>Instant access after payment</TrustItem>
          <TrustItem>Works on Windows & Mac</TrustItem>
          <TrustItem>No monthly limits</TrustItem>
          <TrustItem>Telegram support included</TrustItem>
          <TrustItem>100% private - runs locally</TrustItem>
        </TrustSignals>

        <Footer />
      </Layout>
    </PageBackground>
  );
}

export default WarmerPage; 